import React, { useEffect, useRef } from "react";
import { useTheme } from "@material-ui/core/styles";
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  Switch,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import {
  Close as CloseIcon,
  Replay,
  PlayArrow,
  WarningRounded,
} from "@material-ui/icons";
import { i18n } from "../../../translate/i18n";
import SimBubble from "./SimBubble";
import SimSideEffectCard from "./SimSideEffectCard";
import SimPrompt from "./SimPrompt";
import SimContactForm from "./SimContactForm";
import SimChannelSelector from "./SimChannelSelector";
import SimVariablesPanel from "./SimVariablesPanel";

/**
 * Panel de previsualización del bot (0383 FS-2.3).
 *
 * Dos planos separados a propósito: las burbujas son lo que vería el cliente; las tarjetas
 * punteadas, lo que haría el sistema por dentro. El banner recuerda en todo momento que nada
 * se está enviando (R5).
 */
const FlowSimulatorPanel = ({
  open,
  loading,
  session,
  visibleSteps,
  isPlaying,
  provider,
  contact,
  setContact,
  options,
  setOptions,
  whatsappId,
  setWhatsappId,
  onReply,
  onRestart,
  onClose,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isDark = theme.palette.type === "dark";
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [visibleSteps.length]);

  const statusChip = () => {
    if (!session) return null;
    const map = {
      waiting: {
        label: i18n.t("flowbuilders.simulator.status.waiting") || "Esperando respuesta",
        color: theme.palette.warning.main,
      },
      completed: {
        label: i18n.t("flowbuilders.simulator.status.completed") || "Flujo completado",
        color: theme.palette.success.main,
      },
      error: {
        label: i18n.t("flowbuilders.simulator.status.error") || "Error",
        color: theme.palette.error.main,
      },
      running: {
        label: i18n.t("flowbuilders.simulator.status.running") || "En ejecución",
        color: theme.palette.info.main,
      },
    };
    const cfg = map[session.status] || map.running;
    return (
      <Chip
        size="small"
        label={cfg.label}
        style={{ backgroundColor: cfg.color, color: "#fff", fontWeight: 600 }}
      />
    );
  };

  const content = (
    <Box display="flex" flexDirection="column" height="100%" style={{ minWidth: 0 }}>
      {/* Cabecera */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        style={{ padding: theme.spacing(1, 1, 1, 1.5), gap: 8 }}
      >
        <Box display="flex" alignItems="center" style={{ gap: 8, minWidth: 0 }}>
          <PlayArrow style={{ color: theme.palette.primary.main }} />
          <Typography variant="subtitle2" style={{ fontWeight: 600 }} noWrap>
            {i18n.t("flowbuilders.simulator.title") || "Previsualizar bot"}
          </Typography>
          {statusChip()}
        </Box>
        <Box display="flex" alignItems="center">
          <Tooltip title={i18n.t("flowbuilders.simulator.restart") || "Reiniciar"}>
            <span>
              <IconButton size="small" onClick={onRestart} disabled={loading}>
                <Replay fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Aviso permanente: nada se envía (R5) */}
      <Box
        style={{
          backgroundColor: isDark ? "rgba(255,193,7,0.12)" : "#fff8e1",
          borderTop: `1px solid ${theme.palette.divider}`,
          borderBottom: `1px solid ${theme.palette.divider}`,
          padding: theme.spacing(0.75, 1.5),
        }}
        display="flex"
        alignItems="center"
      >
        <WarningRounded
          style={{ fontSize: 16, color: theme.palette.warning.main, marginRight: 6 }}
        />
        <Typography variant="caption" style={{ color: theme.palette.text.secondary }}>
          {i18n.t("flowbuilders.simulator.banner") ||
            "Simulación: no se envía ningún mensaje real ni se modifica ningún ticket."}
        </Typography>
      </Box>

      {/* Configuración */}
      <Box style={{ padding: theme.spacing(1.25) }}>
        <Box mb={1}>
          <SimChannelSelector
            provider={provider}
            value={whatsappId}
            onChange={setWhatsappId}
            disabled={Boolean(session) || loading}
          />
        </Box>
        <SimContactForm
          contact={contact}
          setContact={setContact}
          disabled={Boolean(session) || loading}
        />
        <Box mt={0.5}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                color="primary"
                checked={options.executeHttp}
                disabled={Boolean(session) || loading}
                onChange={(e) =>
                  setOptions((prev) => ({ ...prev, executeHttp: e.target.checked }))
                }
              />
            }
            label={
              <Typography variant="caption">
                {i18n.t("flowbuilders.simulator.executeHttp") ||
                  "Ejecutar de verdad las llamadas HTTP"}
              </Typography>
            }
          />
          <FormControlLabel
            control={
              <Switch
                size="small"
                color="primary"
                checked={options.executeAi}
                disabled={Boolean(session) || loading}
                onChange={(e) =>
                  setOptions((prev) => ({ ...prev, executeAi: e.target.checked }))
                }
              />
            }
            label={
              <Typography variant="caption">
                {i18n.t("flowbuilders.simulator.executeAi") ||
                  "Ejecutar de verdad los nodos de IA (consume tokens)"}
              </Typography>
            }
          />
        </Box>
      </Box>

      <Divider />

      {/* Conversación */}
      <Box
        ref={scrollRef}
        flex={1}
        style={{
          overflowY: "auto",
          padding: theme.spacing(1.25),
          backgroundColor: isDark ? "#0b141a" : "#efeae2",
        }}
      >
        {!session && !loading && (
          <Typography
            variant="caption"
            style={{ color: theme.palette.text.secondary }}
          >
            {i18n.t("flowbuilders.simulator.empty") ||
              "Pulsa Previsualizar para recorrer el flujo paso a paso."}
          </Typography>
        )}

        {visibleSteps.map((step) =>
          step.preview ? (
            <React.Fragment key={`s-${step.seq}`}>
              <SimBubble step={step} />
              {step.warning && (
                <Typography
                  variant="caption"
                  style={{
                    display: "block",
                    color: theme.palette.warning.main,
                    marginBottom: 8,
                  }}
                >
                  ⚠️ {step.warning}
                </Typography>
              )}
            </React.Fragment>
          ) : (
            <React.Fragment key={`s-${step.seq}`}>
              <SimSideEffectCard step={step} />
              {step.warning && (
                <Typography
                  variant="caption"
                  style={{
                    display: "block",
                    color: theme.palette.warning.main,
                    marginBottom: 8,
                  }}
                >
                  ⚠️ {step.warning}
                </Typography>
              )}
            </React.Fragment>
          )
        )}

        {(loading || isPlaying) && (
          <Box display="flex" alignItems="center" style={{ gap: 8, marginTop: 8 }}>
            <CircularProgress size={14} />
            <Typography variant="caption" style={{ color: theme.palette.text.secondary }}>
              {i18n.t("flowbuilders.simulator.running") || "Recorriendo el flujo…"}
            </Typography>
          </Box>
        )}

        {session?.status === "error" && session?.errorMessage && (
          <Box mt={1} style={{ color: theme.palette.error.main }}>
            <Typography variant="caption">{session.errorMessage}</Typography>
          </Box>
        )}
      </Box>

      <SimVariablesPanel variables={session?.variables} />

      {session?.status === "waiting" && !isPlaying && (
        <SimPrompt
          awaiting={session.awaiting}
          disabled={loading}
          onReply={onReply}
        />
      )}
    </Box>
  );

  if (isMobile) {
    return (
      <Drawer anchor="bottom" open={open} onClose={onClose}>
        <Box style={{ height: "85vh" }}>{content}</Box>
      </Drawer>
    );
  }

  if (!open) return null;

  return (
    <Box
      style={{
        width: 380,
        flexShrink: 0,
        borderLeft: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        height: "100%",
      }}
    >
      {content}
    </Box>
  );
};

export default FlowSimulatorPanel;
